import { lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';
import { budgetingSections } from './sections';
import type { BudgetingSectionDef } from './context';

// Pages and tabs load when first opened.
const BudgetingHomePage = lazy(() => import('./pages/BudgetingHomePage'));
const BudgetingSectionPage = lazy(() => import('./pages/BudgetingSectionPage'));
const SubmissionPage = lazy(() => import('./pages/SubmissionPage'));

/**
 * Budgeting's routes (part of the Accounting module), spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages meeting 403s.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Budgeting.View}>
    <RequireModule module={Modules.Accounting}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: BudgetingSectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<BudgetingSectionPage section={def as unknown as BudgetingSectionDef} />) });

export const budgetingRoutes: RouteObject[] = [
  { path: 'budgeting', element: guard(<BudgetingHomePage />) },
  section(budgetingSections.cycle, 'budgeting/cycle/:view?'),
  section(budgetingSections.preparation, 'budgeting/preparation/:view?'),
  { path: 'budgeting/submission/:id', element: guard(<SubmissionPage />) },
  section(budgetingSections.methods, 'budgeting/methods/:view?'),
  section(budgetingSections.review, 'budgeting/review/:view?'),
  section(budgetingSections.execution, 'budgeting/execution/:view?'),
  section(budgetingSections.changes, 'budgeting/changes/:view?'),
  section(budgetingSections.performance, 'budgeting/performance/:view?'),
  section(budgetingSections.company, 'budgeting/company/:view?'),
  section(budgetingSections.structure, 'budgeting/structure/:view?'),
  section(budgetingSections.settings, 'budgeting/settings/:view?'),
];
